import React, { useId, useState } from "react";
import { X } from "lucide-react";

/**
 * Chip-based tag input.
 *
 * Behaviour:
 *  - Comma / Enter / blur commits the current draft as chips.
 *  - Backspace on an empty draft removes the last chip.
 *  - The × button on a chip removes only that specific chip (by index).
 *
 * Tags are uppercased and deduped on commit.
 *
 * Important layout note — the outer container MUST NOT be a `<label>`.
 * `<label>` implicitly associates with the first labelable descendant
 * (`<button>` counts) which, in our case, would be the first chip's remove
 * button. Any click inside the label then fires a synthetic click on THAT
 * first remove button, deleting an extra chip. We use a `<div>` wrapper and
 * put a proper `<label htmlFor>` only around the eyebrow text, bound to the
 * text input by a unique id.
 */
export default function TagInput({ label, value, onChange, testid, placeholder }) {
  const tags = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const inputId = useId();

  const commit = (raw) => {
    const additions = raw
      .split(",")
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean);
    if (!additions.length) return;
    const next = [...tags];
    const seen = new Set(next.map((t) => String(t).toUpperCase()));
    for (const a of additions) {
      if (!seen.has(a)) {
        next.push(a);
        seen.add(a);
      }
    }
    onChange(next);
    setDraft("");
  };

  return (
    <div className="block">
      <label htmlFor={inputId} className="upr-eyebrow block mb-2">{label}</label>
      <div
        className="p-2 border flex flex-wrap gap-1.5 items-center min-h-[42px]"
        style={{ borderColor: "var(--upr-border)" }}
        data-testid={testid}
      >
        {tags.map((t, i) => (
          <span
            key={`${testid}-chip-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-heading uppercase tracking-widest"
            style={{ background: "var(--upr-fg)", color: "var(--upr-bg)" }}
            data-testid={`${testid}-chip-${i}`}
          >
            {t}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChange(tags.filter((_, j) => j !== i))}
              aria-label={`Remove ${t}`}
              data-testid={`${testid}-chip-${i}-remove`}
              style={{ lineHeight: 0 }}
            ><X size={10} /></button>
          </span>
        ))}
        <input
          id={inputId}
          type="text"
          value={draft}
          placeholder={tags.length ? "" : (placeholder || "HOUSE, DEEP HOUSE, LISBON")}
          onChange={(e) => {
            const v = e.target.value;
            if (v.endsWith(",")) commit(v); else setDraft(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(draft); }
            else if (e.key === "Backspace" && draft === "" && tags.length) {
              onChange(tags.slice(0, -1));
            }
          }}
          onBlur={() => draft && commit(draft)}
          data-testid={`${testid}-input`}
          className="flex-1 min-w-[120px] p-1 bg-transparent focus:outline-none"
        />
      </div>
    </div>
  );
}
